import {
	RiArrowRightUpLine,
	RiCheckboxCircleLine,
	RiCheckLine,
	RiCloseCircleLine,
	RiGroupLine,
	RiMailSendLine,
	RiPlugLine,
	RiPriceTagLine,
	RiRefreshLine,
	RiTimeLine,
} from "@remixicon/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { $api } from "@/api/query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_app/dashboard")({
	component: DashboardPage,
});

function StatCard({
	title,
	value,
	hint,
	icon: Icon,
	href,
	loading,
}: {
	title: string;
	value: string | number;
	/// Small unit/denominator line under the value (e.g. "of 18,940 SKUs").
	hint?: string;
	icon: React.ElementType;
	href: string;
	loading?: boolean;
}) {
	return (
		<Link to={href}>
			<Card className="hover:bg-muted/30 transition-colors group">
				<CardHeader className="flex flex-row items-center justify-between pb-2">
					<CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
					<Icon className="size-4 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					{loading ? (
						<Skeleton className="h-8 w-16" />
					) : (
						<div className="flex items-center justify-between">
							<div className="flex flex-col">
								<span className="text-2xl font-bold">{value}</span>
								{hint && <span className="text-xs text-muted-foreground">{hint}</span>}
							</div>
							<RiArrowRightUpLine className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
						</div>
					)}
				</CardContent>
			</Card>
		</Link>
	);
}

interface DashboardMember {
	id: string;
	name: string;
	email: string;
}

function DashboardPage() {
	const { data: membersData, isLoading: membersLoading } = useQuery({
		queryKey: ["auth", "org", "members"],
		queryFn: async (): Promise<DashboardMember[]> => {
			const res = await fetch("/auth/org/members", { credentials: "include" });
			if (!res.ok) return [];
			const body = (await res.json()) as { members: DashboardMember[] };
			return body.members ?? [];
		},
	});
	const { data: enrichmentData, isLoading: enrichmentLoading } = $api.useQuery(
		"get",
		"/api/v1/prohandel/enrichment/stats",
	);
	const { data: syncData, isLoading: syncLoading } = $api.useQuery(
		"get",
		"/api/v1/prohandel/sync/status",
	);
	// `/notifications` returns a paginated envelope, never a bare array.
	// Ask for exactly the 6 rows this card can use: 5 render, and a 6th
	// only decides whether the "View all" link shows.
	const { data: notifData } = $api.useQuery("get", "/api/v1/notifications", {
		params: { query: { limit: 6, offset: 0 } },
	});
	const { data: unreadData } = $api.useQuery("get", "/api/v1/notifications/unread-count");

	const members = Array.isArray(membersData) ? membersData : [];
	const enrichment = enrichmentData as
		| {
				total?: number;
				enriched?: number;
				exportEnabled?: number;
				totalRows?: number;
		  }
		| undefined;
	const syncStatuses = Array.isArray(syncData)
		? (syncData as Array<{
				resourceName: string;
				lastChangeId: number;
				lastSyncedAt: string | null;
			}>)
		: [];
	const notifications = notifData?.data ?? [];
	// Org-wide unread count, not the unread subset of the 6 rows fetched above.
	const unreadCount = unreadData?.count ?? 0;

	const articleSync = syncStatuses.find((s) => s.resourceName === "article");
	const lastSynced = articleSync?.lastSyncedAt ? timeAgo(articleSync.lastSyncedAt) : "Never";

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
				<p className="text-muted-foreground text-sm mt-1">Overview of your workspace</p>
			</div>

			{/* Stat cards */}
			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
				<StatCard
					title="Articles"
					value={enrichment?.total ?? 0}
					hint="products with stock"
					icon={RiPriceTagLine}
					href="/catalogue/editor"
					loading={enrichmentLoading}
				/>
				<StatCard
					title="Enriched"
					value={enrichment?.enriched ?? 0}
					hint={`of ${enrichment?.totalRows ?? 0} SKUs (sizes)`}
					icon={RiCheckboxCircleLine}
					href="/catalogue/editor"
					loading={enrichmentLoading}
				/>
				<StatCard
					title="Export Enabled"
					value={enrichment?.exportEnabled ?? 0}
					hint={`of ${enrichment?.totalRows ?? 0} SKUs (sizes)`}
					icon={RiRefreshLine}
					href="/catalogue/editor"
					loading={enrichmentLoading}
				/>
				<StatCard
					title="Team Members"
					value={members.length}
					icon={RiGroupLine}
					href="/settings?tab=members"
					loading={membersLoading}
				/>
			</div>

			<div className="grid gap-6 lg:grid-cols-3">
				{/* Quick actions */}
				<Card className="lg:col-span-1">
					<CardHeader>
						<CardTitle className="text-base">Quick Actions</CardTitle>
					</CardHeader>
					<CardContent className="grid gap-2">
						<Link to="/catalogue/editor">
							<Button variant="outline" className="w-full justify-start gap-2">
								<RiPriceTagLine className="size-4" />
								Open Catalogue Editor
							</Button>
						</Link>
						<Link to="/integrations">
							<Button variant="outline" className="w-full justify-start gap-2">
								<RiPlugLine className="size-4" />
								Configure Integrations
							</Button>
						</Link>
						<Link to="/settings" search={{ tab: "members" }}>
							<Button variant="outline" className="w-full justify-start gap-2">
								<RiMailSendLine className="size-4" />
								Invite Team Member
							</Button>
						</Link>
					</CardContent>
				</Card>

				{/* Sync status + recent activity */}
				<Card className="lg:col-span-2">
					<CardHeader className="flex flex-row items-center justify-between">
						<CardTitle className="text-base">Recent Activity</CardTitle>
						{unreadCount > 0 && (
							<Badge variant="secondary" className="text-xs">
								{unreadCount} unread
							</Badge>
						)}
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Sync status row */}
						<div className="flex items-center gap-3 text-sm rounded-md border p-3 bg-muted/20">
							<RiRefreshLine className="size-4 text-muted-foreground shrink-0" />
							<div className="flex-1 min-w-0">
								<p className="font-medium">Article Sync</p>
								<p className="text-muted-foreground text-xs">
									{syncLoading ? "Loading..." : <>Last synced: {lastSynced}</>}
								</p>
							</div>
							{articleSync?.lastSyncedAt ? (
								<RiCheckboxCircleLine className="size-4 text-green-500 shrink-0" />
							) : (
								<RiCloseCircleLine className="size-4 text-muted-foreground shrink-0" />
							)}
						</div>

						{/* Notifications */}
						{notifications.length === 0 ? (
							<p className="text-sm text-muted-foreground py-2 text-center">
								No activity yet. Actions from your team will appear here.
							</p>
						) : (
							<div className="space-y-3">
								{notifications.slice(0, 5).map((n) => (
									<div key={n.id} className="flex items-start gap-3 text-sm">
										<div className="mt-0.5">
											{n.read ? (
												<RiCheckLine className="size-4 text-muted-foreground" />
											) : (
												<RiTimeLine className="size-4 text-primary" />
											)}
										</div>
										<div className="flex-1 min-w-0">
											<p className={n.read ? "text-muted-foreground" : "font-medium"}>{n.title}</p>
											{n.body && <p className="text-muted-foreground text-xs truncate">{n.body}</p>}
										</div>
										<span className="text-xs text-muted-foreground whitespace-nowrap">
											{timeAgo(n.createdAt)}
										</span>
									</div>
								))}
								{notifications.length > 5 && (
									<Link
										to="/notifications"
										className="block text-xs text-primary hover:underline pt-1"
									>
										View all notifications
									</Link>
								)}
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

function timeAgo(dateStr: string): string {
	const diff = Date.now() - new Date(dateStr).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.floor(hrs / 24);
	return `${days}d ago`;
}
